import Image from 'next/image';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { EnvelopeIcon } from '@heroicons/react/20/solid';
import { ElementRef, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { BodySmall } from '@common/body';
import { H4, H5 } from '@common/headline';
import {
  InputWithLeadingIconAndInLineValidationError,
  InputWrapper,
  ResizableInputErrorMessage,
} from '../../NhsSignup/Inputs';
import { emailValidationRules } from '../../NhsSignup/constants/validation';
import Cross from '@common/Icons/Cross';
import { getErrorMessage } from '../../NhsSignup/helpers';
import {
  aliasUserInBraze,
  logEventInBraze,
  setEmailInBraze,
  setUserInBraze,
} from '@nextUtils/brazeUtils';
import NotificationHandler from '../../NotificationHandler';
import { EmailListType } from '@nextTypes/emailList';
import { LoadingSpinner } from '../../Checkout/LoadingSpinner';
import { mixpanelTrackNext } from '@common/mixpanel';

import { getAPIUrl } from '@nextUtils/urlUtils';

import mealPlan from '@public/img/articles/mealPlan.png';

const guideMap = {
  medicated: {
    title:
      'Maximise your weight loss on Mounjaro with our free 7-day meal plan',
    description:
      'Get expert advice from our team of registered dietitians to make losing weight feel easier while on medication. Subscribe to our newsletter to get access today.',
  },
  nonmedicated: {
    title: 'Download our free, indulgent 7-day meal plan',
    description:
      'It includes expert advice from our team of registered dietitians to make losing weight feel easier. Subscribe to our newsletter to get access today.',
  },
};

type ButtonState = 'idle' | 'loading';

const buttonCopy: {
  [key in ButtonState]: JSX.Element;
} = {
  idle: <H5>Get my free meal plan</H5>,
  loading: (
    <div className="mr-2 h-5 w-5">
      <LoadingSpinner colour="white" />
    </div>
  ),
};

const SlideUpModal = ({
  guideType,
  guideCategory,
  guideTitle,
  scrollPercentage,
}: {
  guideType: 'medicated' | 'nonmedicated';
  guideCategory: string;
  guideTitle: string;
  scrollPercentage: number;
}): JSX.Element => {
  const [showModal, setShowModal] = useState(false);
  const { asPath } = useRouter();
  const modalHasBeenShownToTheUserPreviously = Cookies.get('modal-shown');
  const [buttonState, setButtonState] = useState<ButtonState>('idle');
  const notificationHandler = useRef<null | ElementRef<
    typeof NotificationHandler
  >>(null);
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<{
    email: string;
    termsAndConditions: boolean;
  }>({
    mode: 'onBlur',
  });

  // Display the modal after the user has scrolled through 50% of the content
  useEffect(() => {
    if (
      scrollPercentage >= 50 &&
      !showModal &&
      !modalHasBeenShownToTheUserPreviously
    ) {
      setShowModal(true);
      mixpanelTrackNext('Guide modal opened', {
        guideType,
        guideCategory,
        guideTitle,
        guideURL: asPath,
      });
    }
  }, [
    asPath,
    guideCategory,
    guideTitle,
    guideType,
    modalHasBeenShownToTheUserPreviously,
    scrollPercentage,
    showModal,
  ]);

  const onSubmit = async (data: { email: string }) => {
    setButtonState('loading');
    try {
      const { email } = data;

      const response = await fetch(`${getAPIUrl()}/api/emaillist/guides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          selectedCategory: guideType,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to sign up for recipe email list');
      }
      const { emailList }: { emailList: EmailListType } = await response.json();

      if (window.mixpanel?.alias) {
        window.mixpanel.alias(email);
        window.mixpanel.people.set({
          $email: email,
          $braze_external_id: emailList._id, // eslint-disable-line camelcase
        });
        mixpanelTrackNext('Guide email collection', {
          selectedCategory: guideType,
          email,
          guideCategory,
          guideTitle,
          guideURL: asPath,
        });
      }
      setHasSubmittedEmail(true);
      await setUserInBraze(emailList._id);
      await aliasUserInBraze(email, 'email_list_email');
      await setEmailInBraze(email);
      await logEventInBraze(
        'Guide email list sign up: 7-day meal plan',
        emailList._id,
      );
    } catch (error) {
      setButtonState('idle');
      if (notificationHandler.current) {
        notificationHandler.current.showNotification(
          'Failed to sign up for recipe email list',
          'error',
        );
      }
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
    Cookies.set('modal-shown', 'true', { expires: 365 });
    mixpanelTrackNext('Guide modal closed', {});
  };

  return (
    <>
      <NotificationHandler ref={notificationHandler} />
      <motion.div
        initial="hidden"
        animate={showModal ? 'visible' : 'hidden'}
        variants={{
          hidden: { y: '100%', opacity: 0 },
          visible: { y: 0, opacity: 1 },
        }}
        transition={{ duration: 0.5, type: 'spring' }}
        className="fixed inset-x-0 bottom-0 z-10"
      >
        <div className="relative m-auto max-w-[680px] rounded-t-2xl bg-blue p-4 text-white">
          <div className="absolute left-2 top-2 cursor-pointer">
            <Cross stroke="white" onClick={onCloseModal} />
          </div>
          <div className="absolute -top-10 right-2">
            <Image src={mealPlan} alt="Meal Plan" width={130} height={102} />
          </div>
          <div className="mt-5 flex max-w-[65%] flex-row">
            <H4>{guideMap[guideType]?.title}</H4>
          </div>
          <div className="mb-8 mt-1">
            <BodySmall>{guideMap[guideType]?.description}</BodySmall>
          </div>
          {hasSubmittedEmail ? (
            <div className="pb-8 text-center text-system-success">
              <H5>We{"'"}ve sent the meal plan to your inbox!</H5>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <InputWrapper>
                <InputWithLeadingIconAndInLineValidationError
                  {...register('email', emailValidationRules)}
                  type="email"
                  id="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  data-cy="email"
                  hasError={!!getErrorMessage(errors, 'email')}
                >
                  <EnvelopeIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </InputWithLeadingIconAndInLineValidationError>
              </InputWrapper>
              <div className="mt-2 flex items-center pl-1">
                <input
                  id="terms-and-conditions"
                  type="checkbox"
                  {...register('termsAndConditions', {
                    required: 'This is required',
                  })}
                  className="form-checkbox h-4 w-4 rounded border-gray-300 text-dawn-blue"
                  data-cy="terms-and-conditions"
                />
                <div className="pl-2 text-mono-white">
                  <BodySmall>
                    I{"'"}ve read and agreed to the Terms of Service & Privacy
                    Policy.
                  </BodySmall>
                </div>
              </div>
              <ResizableInputErrorMessage
                error={
                  getErrorMessage(errors, 'email') ||
                  getErrorMessage(errors, 'termsAndConditions')
                }
              />
              <div className="mt-3 flex flex-1">
                <button className="relative flex min-h-[52px] flex-1 justify-center overflow-hidden rounded bg-teal px-5 py-4">
                  {buttonCopy[buttonState]}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default SlideUpModal;
